import React from 'react';
import EntityTable from './EntityTable';
import StatusPill from './StatusPill';
import { formatByHint } from './criteriaBarUtils';

// Table replacement for the per-metric "… por ventana" bar charts (Roas/Cpa/Ctr
// WindowsChart). Renders one row per window through the shared EntityTable so it
// matches the platform's other card tables (peer comparison, sub-items, …).
//
// Faithful to the charts it supersedes: same title per metric, the target shown
// as the table subtitle (it was the dashed line), `is_critical` flagged in red
// (it was the rose bar), and a CTR 7-vs-7 delta in the subtitle when there is no
// target. Values are formatted strictly by `format_hint` — never by metric name.

function titleForMetric(metricKey, metricLabel) {
  const k = String(metricKey || '').toLowerCase();
  if (k.includes('cpa')) return 'CPA por ventana';
  if (k.includes('roas')) return 'ROAS por ventana';
  if (k.includes('ctr')) return 'CTR por ventana';
  return metricLabel ? `${metricLabel} por ventana` : 'Comparativa por ventana';
}

export default function WindowComparisonTable({ windowComparison, target, currency = 'USD' }) {
  if (!windowComparison || typeof windowComparison !== 'object') return null;
  const windows = Array.isArray(windowComparison.windows) ? windowComparison.windows : [];
  if (windows.length < 2) return null;

  const metricKey = String(windowComparison.metric_key || '').toLowerCase();
  const formatHint = windowComparison.format_hint;
  const fmt = (n) => {
    const num = typeof n === 'number' ? n : Number(n);
    return Number.isFinite(num) ? formatByHint(num, formatHint, currency) : '—';
  };

  const targetNum = typeof target === 'number' ? target : Number(target);
  const hasTarget = Number.isFinite(targetNum) && targetNum > 0;
  // Tone for the "vs objetivo" column: CPA is lower-is-better, ROAS/CTR higher-is-better.
  const lowerIsBetter = metricKey.includes('cpa');

  const rows = windows.map((win, idx) => {
    const value = typeof win?.value === 'number' ? win.value : Number(win?.value);
    return {
      key: win?.key ?? `${win?.label ?? 'win'}-${idx}`,
      label: String(win?.label || (idx === 0 ? 'Ventana A' : 'Ventana B')),
      value: Number.isFinite(value) ? value : null,
      isCritical: win?.is_critical === true,
    };
  });

  // CTR (and any metric without a target) gets a 7-vs-7 style delta in the subtitle,
  // mirroring the chart's chip: short window (windows[0]) vs baseline (windows[1]).
  const a = rows[0]?.value;
  const b = rows[1]?.value;
  const deltaVsBase = Number.isFinite(a) && Number.isFinite(b) && b !== 0 ? ((a / b) - 1) * 100 : null;

  const description = hasTarget
    ? `Objetivo: ${fmt(targetNum)}`
    : deltaVsBase != null
      ? `${deltaVsBase >= 0 ? '+' : '−'}${Math.abs(deltaVsBase).toFixed(1)}% vs ventana base`
      : windowComparison.metric_label || null;

  const columns = [
    {
      key: 'ventana',
      label: 'Ventana',
      colSpan: 2,
      render: (r) => (
        <div className="flex items-center gap-2 min-w-0">
          <span className="truncate text-white font-semibold" title={r.label}>{r.label}</span>
          {r.isCritical ? <StatusPill label="Crítico" tone="danger" /> : null}
        </div>
      ),
    },
    {
      key: 'valor',
      label: 'Valor',
      align: 'right',
      render: (r) => (
        <span className={`font-semibold tabular-nums ${r.isCritical ? 'text-rose-300' : 'text-white'}`}>
          {r.value == null ? '—' : fmt(r.value)}
        </span>
      ),
    },
  ];

  if (hasTarget) {
    columns.push({
      key: 'vsTarget',
      label: 'vs objetivo',
      align: 'right',
      render: (r) => {
        if (r.value == null) return '—';
        const meets = lowerIsBetter ? r.value <= targetNum : r.value >= targetNum;
        const deltaFromTarget = ((r.value / targetNum) - 1) * 100;
        const sign = deltaFromTarget >= 0 ? '+' : '−';
        return (
          <span className={`tabular-nums text-[11px] font-semibold ${meets ? 'text-emerald-300' : 'text-rose-300'}`}>
            {sign}{Math.abs(deltaFromTarget).toFixed(0)}%
          </span>
        );
      },
    });
  }

  return (
    <EntityTable
      title={titleForMetric(windowComparison.metric_key, windowComparison.metric_label)}
      description={description}
      rows={rows}
      columns={columns}
      getRowKey={(r) => r.key}
      getRowVariant={(r) => (r.isCritical ? 'danger' : 'default')}
    />
  );
}
